import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { LanguageProvider } from "./context/LanguageContext";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "./components/ui/toaster";

// Landing Page Components
import Header from "./components/Header";
import HeroSection from "./components/HeroSection";
import FeatureCards from "./components/FeatureCards";
import AppShowcase from "./components/AppShowcase";
import FounderSection from "./components/FounderSection";
import BookSection from "./components/BookSection";
import FormulaSection from "./components/FormulaSection";
import Newsletter from "./components/Newsletter";
import Footer from "./components/Footer";

// App Pages
import AuthPage from "./pages/AuthPage";
import Dashboard from "./pages/Dashboard";
import ProgramsPage from "./pages/ProgramsPage";
import ProgramDetailPage from "./pages/ProgramDetailPage";
import WorkoutDetailPage from "./pages/WorkoutDetailPage";
import ProfilePage from "./pages/ProfilePage";
import AchievementsPage from "./pages/AchievementsPage";
import WellbeingPage from "./pages/WellbeingPage";
import MeditationDetailPage from "./pages/MeditationDetailPage";
import NutritionPage from "./pages/NutritionPage";
import RecipeDetailPage from "./pages/RecipeDetailPage";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#0d1f1f] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-emerald-500/30 border-t-emerald-500 rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/auth" replace />;
  }
  
  return children;
};

// Public Route (redirect to dashboard if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#0d1f1f] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-emerald-500/30 border-t-emerald-500 rounded-full animate-spin" />
      </div>
    );
  }
  
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

const Home = () => {
  return (
    <div className="min-h-screen bg-[#0d1f1f]">
      <Header />
      <HeroSection />
      <FeatureCards />
      <AppShowcase />
      <FounderSection />
      <BookSection />
      <FormulaSection />
      <Newsletter />
      <Footer />
    </div>
  );
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<Home />} />
      <Route 
        path="/auth" 
        element={
          <PublicRoute>
            <AuthPage />
          </PublicRoute>
        } 
      />
      
      {/* Protected Routes */}
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/programs" 
        element={
          <ProtectedRoute>
            <ProgramsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/programs/:programId" 
        element={
          <ProtectedRoute>
            <ProgramDetailPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/workout/:workoutId" 
        element={
          <ProtectedRoute>
            <WorkoutDetailPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/profile" 
        element={
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/achievements" 
        element={
          <ProtectedRoute>
            <AchievementsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/wellbeing" 
        element={
          <ProtectedRoute>
            <WellbeingPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/wellbeing/meditation/:meditationId" 
        element={
          <ProtectedRoute>
            <MeditationDetailPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/nutrition" 
        element={
          <ProtectedRoute>
            <NutritionPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/nutrition/recipe/:recipeId" 
        element={
          <ProtectedRoute>
            <RecipeDetailPage />
          </ProtectedRoute>
        } 
      />
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <BrowserRouter>
          <div className="App">
            <AppRoutes />
            <Toaster />
          </div>
        </BrowserRouter>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
